import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ToastrService } from 'ngx-toastr';
import { distinctUntilChanged, finalize, Subject, Subscription, takeUntil } from 'rxjs';
import { IExportDetails, IExportHistory } from 'src/app/model/export-data.model';
import { ExportPlatformService } from 'src/app/services/export-platform.service';
import { CoreModule } from 'src/app/core/core.module';
import { SharedModule } from '../../../shared/shared.module';
import { CountableTimeZonePipe } from 'countable@helpers';
import { SharedService } from 'src/app/components/shared/shared.service';


@Component({
    selector: 'app-user-export-history',
    templateUrl: './export-history.component.html',
    styleUrls: ['./export-history.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed, void', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
            transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
        ]),
    ],
    standalone: true,
    imports: [SharedModule, CommonModule, CoreModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserExportHistoryComponent implements OnInit, OnDestroy {
    @ViewChild('deleteFilePopup') deleteFilePopup: TemplateRef<any>;
    @ViewChild('deleteHistoryPopup') deleteHistoryPopup: TemplateRef<any>;
    @Output() spinnerConfig = new EventEmitter<boolean>(null);
    selection = new SelectionModel<IExportHistory>(true, []);
    public allSubscriptions = new Subscription();
    private readonly unSubscribe$: Subject<void> = new Subject<void>();
    masterData: IExportHistory[] = [];
    historyData: IExportHistory[] = [];
    displayedColumns: string[] = ['select', 'exportedByName', 'exportedAt', 'clientCount', 'engagementCount', 'viewDetails', 'actions'];
    nonActionableColumns: string[] = ['exportedByName', 'exportedAt', 'exportStatus', 'clientCount', 'engagementCount'];
    dataSource: MatTableDataSource<IExportHistory> = new MatTableDataSource<IExportHistory>();
    @ViewChild('parentSort') parentSort: MatSort;
    expandedElement: IExportHistory | null;
    columnFilterData: any;
    selectedFilter: any;
    columnDefs: any;
    deleteIds: string;
    loadHistoryGrid: WritableSignal<boolean> = signal<boolean>(true);
    inProgressCount: number = 0;
    viewDetailColumns: string[] = ['downloadedBY', 'createdAt'];
    detailDataSource: MatTableDataSource<IExportDetails> = new MatTableDataSource<IExportDetails>();
    detailsColumnDefs: any;
    masterDetails: any[] = [];
    tabSubscriptionRef !: Subscription;
    exportStarted: WritableSignal<boolean> = signal<boolean>(false);
    loadDetailGrid: WritableSignal<boolean> = signal<boolean>(true);
    public exportCompleted: WritableSignal<boolean> = signal<boolean>(false);
    public exportRowIds: WritableSignal<string[]> = signal<string[]>(null);

    constructor(
        private exportPlatformService: ExportPlatformService,
        private toaster: ToastrService,
        private sharedService: SharedService,
        private dialog: MatDialog,
        private countableTimeZonePipe: CountableTimeZonePipe
    ) { }
    ngOnInit() {
        this.columnDefs = {
            exportedByName: "Exported By",
            exportedAt: "Exported Date & Time",
            clientCount: 'No. of Clients',
            engagementCount: 'No. of Engagements'
        };
        this.detailsColumnDefs = {
            downloadedBY: "Downloaded By",
            createdAt: "Download Date & Time",
        };
        this.getHistoryData();

        this.tabSubscriptionRef = this.exportPlatformService.navigateToHistoryTab$.pipe(
            takeUntil(this.unSubscribe$)
        ).subscribe(res => {
            if (res) {
                this.exportCompleted.set(true);
                this.getHistoryData(true);
            } else {
                this.initializeFilters();
            }
        });

        this.exportPlatformService.exportList$.pipe(
            takeUntil(this.unSubscribe$)
        ).subscribe({
            next: res => {
                if (res) {
                    const exportDetails = Array.from(res.entries()).map(([key, value]) => ({ key, value }));
                    if (exportDetails && exportDetails.some(r => r?.value === 'STARTED' || r?.value === 'PROGRESS')) {
                        const list = exportDetails.filter(r => (r?.value === 'STARTED' || r?.value === 'PROGRESS'))?.map(r => r?.key);
                        this.exportRowIds.set(list);
                    } else {
                        this.exportRowIds.set(null);
                    }
                } else {
                    this.exportRowIds.set(null);
                }
            }
        });
    }

    initializeFilters() {
        this.selectedFilter = this.sharedService.createAppliedFilterObject(this.nonActionableColumns);
        this.columnFilterData = this.sharedService.createAppliedFilterObject(this.nonActionableColumns);
        this.nonActionableColumns.forEach(ele => {
            this.columnFilterData[ele] = this.sharedService.getColumnFilterData(this.masterData, ele);
        });
        this.historyData = JSON.parse(JSON.stringify(this.sharedService.getFilteredGridData(this.masterData, this.selectedFilter)));
        this.dataSource = new MatTableDataSource(this.historyData);
        this.dataSource.sort = this.parentSort;
        this.selection.clear();
    }

    getHistoryData(updateNavigation: boolean = false) {
        this.spinnerConfig.emit(true);
        this.exportPlatformService.getExportHistoryData().pipe(
            takeUntil(this.unSubscribe$),
            distinctUntilChanged(),
            finalize(() => this.spinnerConfig.emit(false))
        ).subscribe({
            next: (response: any) => {
                if (response && response.data && response.data.length) {
                    this.masterData = JSON.parse(JSON.stringify(response.data));
                    this.masterData.forEach((ele: any) => {
                        ele.exportedAt = ele.exportedAt ? this.countableTimeZonePipe.transform(ele.exportedAt, "MMM dd, yyyy - hh:mm a", null) : '';
                        ele.isChecked = false;
                    });
                    this.inProgressCount = this.masterData.filter(el => el.exportStatus === 'In progress').length;
                    this.exportPlatformService.inProgressCount$.next(this.inProgressCount);
                    this.initializeFilters();
                } else {
                    this.masterData = [];
                    this.historyData = [];
                }
                this.loadHistoryGrid.set(false);
            },
            error: (error: any) => {
                this.loadHistoryGrid.set(false);
                this.toaster.error(error.error);
            }
        }).add(() => {
            if (updateNavigation) {
                this.exportPlatformService.navigateToHistoryTab$.next(false);
            }
        })
    }

    clearFilter(colKey: string) {
        this.selectedFilter[colKey] = [];
        this.columnFilterData[colKey].forEach(ele => {
            ele.isChecked = false;
        })
        this.historyData = JSON.parse(JSON.stringify(this.sharedService.getFilteredGridData(this.masterData, this.selectedFilter)));
        this.dataSource = new MatTableDataSource(this.historyData);
        this.dataSource.sort = this.parentSort;
    }

    onFilterChanged(colKey: string, col: any, isChecked: boolean) {
        col.isChecked = isChecked;
        if (isChecked) {
            this.selectedFilter[colKey].push(col.value);
        } else {
            const index = this.selectedFilter[colKey].indexOf(col.value);
            this.selectedFilter[colKey].splice(index, 1);
        }
        this.historyData = JSON.parse(JSON.stringify(this.sharedService.getFilteredGridData(this.masterData, this.selectedFilter)));
        this.dataSource = new MatTableDataSource(this.historyData);
        this.dataSource.sort = this.parentSort;
    }

    openDeleteHistory() {
        this.deleteIds = this.selection.selected.map(e => e.hash).join(',');;
        const dialogRef = this.dialog.open(this.deleteHistoryPopup, {});
    }

    openDeleteFile(row: IExportHistory) {
        this.deleteIds = row.hash;
        const dialogRef = this.dialog.open(this.deleteFilePopup, {});
    }

    deleteHistory() {
        this.spinnerConfig.emit(true)
        this.allSubscriptions.add(
            this.exportPlatformService.deleteExportHistory(this.deleteIds).pipe(
                takeUntil(this.unSubscribe$),
                distinctUntilChanged(),
                finalize(() => this.spinnerConfig.emit(false))
            ).subscribe({
                next: (response: any) => {
                    if (response) {
                        this.toaster.success('History deleted successfully');
                        this.getHistoryData();
                    }
                },
                error: (error: any) => {
                    this.toaster.error(error.error);
                }
            })
        );
    }

    downloadHistory(rowHash: string) {
        let currentMap = this.exportPlatformService.exportList$.getValue();
        if (currentMap) {
            currentMap.set(rowHash, 'STARTED');
        } else {
            currentMap = new Map<string, 'STARTED' | 'PROGRESS' | 'DONE'>([
                [rowHash, 'STARTED']
            ]);
        }
        this.exportPlatformService.exportList$.next(currentMap);
    }

    getRowDetails(row: IExportHistory) {
        this.loadDetailGrid.set(true);
        this.detailDataSource = new MatTableDataSource([]);
        this.spinnerConfig.emit(true)
        this.allSubscriptions.add(
            this.exportPlatformService.viewDownloadDetails(row.hash).pipe(
                takeUntil(this.unSubscribe$),
                distinctUntilChanged(),
                finalize(() => this.spinnerConfig.emit(false))
            ).subscribe({
                next: (response: any) => {
                    if (response && response.data && response.data.length) {
                        this.masterDetails = JSON.parse(JSON.stringify(response.data));
                        this.masterDetails.forEach((ele: any) => {
                            ele.createdAt = ele.createdAt ? this.countableTimeZonePipe.transform(ele.createdAt, "MMM dd, yyyy - hh:mm a", null) : '';
                            ele['initials'] = this.getNameInitials(ele.downloadedBY);
                        })
                        this.detailDataSource = new MatTableDataSource(this.masterDetails);
                    } else {
                        this.detailDataSource = new MatTableDataSource([]);
                    }
                    this.loadDetailGrid.set(false);
                },
                error: (error: any) => {
                    this.loadDetailGrid.set(false);
                    this.detailDataSource = new MatTableDataSource([]);
                    this.toaster.error(error.error);
                }
            })
        );
    }

    getNameInitials(name: string): string {
        let initials = '';
        const nameArray = name.split(' ');
        initials = nameArray[0].charAt(0).toUpperCase() + nameArray[1].charAt(0).toUpperCase();
        return initials;
    }

    isAllSelected() {
        const completedFailedRows = this.dataSource.data.filter(row => (['Completed', 'Failed'].includes(row.exportStatus)));
        return ((this.selection.selected.length !== 0) && (this.selection.selected.length === completedFailedRows.length));
    }

    masterToggle() {
        this.isAllSelected() ? this.selection.clear() : this.dataSource.data.forEach(row => (['Completed', 'Failed'].includes(row.exportStatus)) && this.selection.select(row));
    }

    customSort(event) {
        const copyArr = JSON.parse(JSON.stringify(this.masterDetails));
        if (event.direction === 'asc') {
            copyArr.sort(function (a, b) {
                if (a[event.active] < b[event.active]) return -1;
                if (a[event.active] > b[event.active]) return 1;
                return 0;
            });
        } else if (event.direction === 'desc') {
            copyArr.sort(function (a, b) {
                if (a[event.active] > b[event.active]) return -1;
                if (a[event.active] < b[event.active]) return 1;
                return 0;
            });
        }
        this.detailDataSource = new MatTableDataSource(copyArr);
    }

    ngOnDestroy(): void {
        this.unSubscribe$.next();
        this.unSubscribe$.complete();
    }
}
